import { IDashboardApi } from '../types/dashboard.types';
import { instance, v2Instance } from './instance';

// Build query string from a params object, filtering out undefined/null/empty values
const toQuery = (params: Record<string, any>): string => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (!entries.length) return '';
  return entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
};

export interface DashboardParams {
  dateRange?: string;
  from?: string;
  till?: string;
  brandId?: string;
  kitchenId?: string;
  channelId?: string;
  limit?: number;
}

const buildQuery = (params?: DashboardParams): string => {
  if (!params) return '';
  return toQuery(params);
};

export const dashboardApi: IDashboardApi & {
  fetchOrderSummary: (params?: DashboardParams) => Promise<any>;
  fetchTopBrands: (params?: DashboardParams) => Promise<any>;
  fetchTopMeals: (params?: DashboardParams) => Promise<any>;
  fetchTopCustomers: (params?: DashboardParams) => Promise<any>;
  fetchTopUsers: (params?: DashboardParams) => Promise<any>;
  fetchRevenueSummary: (params?: DashboardParams) => Promise<any>;
  fetchRecentOrders: (params?: DashboardParams) => Promise<any>;
} = {
  // Legacy methods (kept for backward compat with existing redux thunks)
  getOrderSummary: async ({ query }) =>
    await instance.get(`/admin/order/analytics/summary?${query ? query : ''}`),
  getUserSummary: async (range) =>
    await instance.get(`/admin/order/analytics/user/summary?${range}`),
  getCustomerSummary: async (range) =>
    await instance.get(`/admin/order/analytics/customer/summary?${range}`),
  getFilteredOrderSummary: async (range) =>
    await instance.get(`/admin/order/analytics/summary?${range}`),
  getRevenueSummary: async (range) =>
    await instance.get(`/admin/order/analytics/revenue/summary?${range}`),
  getTopKitchens: async (range) =>
    await instance.get(`/admin/order/analytics/top/kitchens?${range}`),
  getTopLocations: async (range) =>
    await instance.get(`/admin/order/analytics/top/locations?${range}`),
  getTopMeals: async ({ query }) =>
    await instance.get(
      `/admin/order/analytics/top/meals?${query ? query : ''}`
    ),
  getTopBrands: async (range) =>
    await instance.get(`/admin/order/brands/summary?${range}`),
  getTopCustomers: async ({ query }) =>
    await instance.get(
      `/admin/order/analytics/top/customers?${query ? query : ''}`
    ),
  getTopUsers: async ({ query }) =>
    await instance.get(
      `/admin/order/analytics/top/users?${query ? query : ''}`
    ),
  getByPaymentMethod: async ({ query }) =>
    await instance.get(
      `/admin/order/analytics/by-payment-method?${query ? query : ''}`
    ),

  // New clean methods using params objects
  fetchOrderSummary: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/analytics/summary?${q}`);
    return data.data;
  },
  fetchTopBrands: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/brands/summary?${q}`);
    return data.data;
  },
  fetchTopMeals: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/analytics/top/meals?${q}`);
    return data.data;
  },
  fetchTopCustomers: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/analytics/top/customers?${q}`);
    return data.data;
  },
  fetchTopUsers: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/analytics/top/users?${q}`);
    return data.data;
  },
  fetchRevenueSummary: async (params) => {
    const q = buildQuery(params);
    const { data } = await instance.get(`/admin/order/analytics/revenue/summary?${q}`);
    return data.data;
  },
  fetchRecentOrders: async (params) => {
    const { limit, ...rest } = params ?? {};
    const q = buildQuery(rest);
    const count = limit ?? 5;
    const base = `/admin/order/all?count=${count}&page=1`;
    const url = q ? `${base}&${q}` : base;
    const { data } = await v2Instance.get(url);
    return data.data ?? [];
  },
};
